// Wearable unlocks — pin / crown / hat. Progress + eligibility come from the
// backend (`settings.unlock_progress`, see services/unlocks.py); this module
// holds the celebration metadata + copy and the claim call.
import api from "@/lib/api";

const daysWord = (d) => (d === 1 ? "1 day" : `${d} days`);

export const ACCESSORY_UNLOCKS = {
    pin: {
        id: "pin",
        name: "a sakura pin",
        icon: "🌸",
        unlock: "sakura-pin",
        initialMood: "proud",
        finalMood: "happy",
        celebrationMs: 4000,
        confettiVariant: "regular",
        phrase: {
            days: (n) => `${n >= 7 ? "a week" : daysWord(n)} together 🌸 i made you something — a little sakura pin. it's yours to keep.`,
            tasks: (n) => `${n} tasks finished 🌸 here — a sakura pin, from me to you.`,
        },
    },
    crown: {
        id: "crown",
        name: "the gold crown",
        icon: "👑",
        unlock: "crown",
        initialMood: "party",
        finalMood: "proud",
        celebrationMs: 6000,
        confettiVariant: "regular",
        phrase: {
            days: (n) => `${daysWord(n)} together 👑 you kept showing up. that's crown material.`,
            tasks: (n) => `${n} tasks done 👑 you earned this one with your hands.`,
            rice: (n) => `${n} rice balls 👑 a very well-fed mochi crowns you.`,
        },
    },
    hat: {
        id: "hat",
        name: "the birthday hat",
        icon: "🎂",
        unlock: "birthday-hat",
        initialMood: "party",
        finalMood: "cozy",
        celebrationMs: 7000,
        confettiVariant: "gold",
        phrase: {
            days: (n) => `${daysWord(n)} together 🎂 party hat time.`,
            streak: (n) => `a ${n}-day streak 🎂 that deserves a party hat.`,
            rice: (n) => `${n} rice balls 🎂 mochi is throwing you a party.`,
            birthday: () => "it's your birthday 🎂 mochi got dressed up for you.",
        },
    },
};

export const UNLOCK_ORDER = ["pin", "crown", "hat"];

// First wearable that's been earned but not yet claimed, as a celebration
// descriptor compatible with <MilestoneCelebration>.
export function pickPendingUnlock(settings) {
    const progress = settings?.unlock_progress;
    if (!progress) return null;
    for (const id of UNLOCK_ORDER) {
        const entry = progress[id];
        if (!entry || entry.unlocked || !entry.eligible) continue;
        const meta = ACCESSORY_UNLOCKS[id];
        const path = entry.met_path || "days";
        const target = entry.paths.find((p) => p.id === path)?.target ?? 0;
        const phraseFn = meta.phrase[path] || meta.phrase.days;
        return { kind: "unlock", ...meta, phrase: phraseFn(target), path };
    }
    return null;
}

export async function claimUnlock(item) {
    const res = await api.post(`/unlocks/${item}/claim`);
    return res.data; // { settings, chat_text, path }
}

// Human line for a path, e.g. "27 / 50 tasks finished".
export function pathLine(p) {
    if (p.id === "birthday") return p.configured ? "or on your birthday 🎂" : "or on your birthday (set it below) 🎂";
    const unit = p.id === "days" ? "days" : p.id === "streak" ? "day streak" : p.label;
    return `${Math.min(p.current, p.target)} / ${p.target} ${unit}`;
}
